import type { ReactNode } from 'react'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'

import { cn } from '@/lib/cn'

interface MenuProps {
  trigger: (props: { open: boolean; toggle: () => void }) => ReactNode
  children: (close: () => void) => ReactNode
  align?: 'left' | 'right'
}

/** 菜单和视口边缘之间至少留这么多像素 */
const EDGE_GAP = 8

/**
 * 点击外部或按 Esc 关闭的下拉菜单。
 *
 * 默认向下展开，**下方放不下时自动翻到上方**。触发器贴着屏幕底部的场景
 * 不止一处（侧栏底部的账号菜单、底部导航、时间线最后一条帖子的更多按钮），
 * 与其让每个调用方自己判断，不如在这里量一次。
 */
export function Menu({ trigger, children, align = 'right' }: MenuProps) {
  const [open, setOpen] = useState(false)
  const [flipped, setFlipped] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  // 量一次实际位置再决定朝向：下方装不下、而上方装得下，就翻上去
  useLayoutEffect(() => {
    if (!open) {
      setFlipped(false)
      return
    }
    const menu = menuRef.current
    const root = rootRef.current
    if (!menu || !root) return
    const menuRect = menu.getBoundingClientRect()
    const overflowsBelow = menuRect.bottom > window.innerHeight - EDGE_GAP
    const spaceAbove = root.getBoundingClientRect().top
    setFlipped(overflowsBelow && spaceAbove > menuRect.height + EDGE_GAP)
  }, [open])

  return (
    <div ref={rootRef} className="relative" onClick={(event) => event.stopPropagation()}>
      {trigger({ open, toggle: () => setOpen((value) => !value) })}
      {open && (
        <div
          ref={menuRef}
          role="menu"
          className={cn(
            'absolute z-30 min-w-44 overflow-hidden rounded-md bg-surface py-1 shadow-lg',
            'border border-line',
            align === 'right' ? 'right-0' : 'left-0',
            flipped ? 'bottom-full mb-1' : 'mt-1',
          )}
        >
          {children(() => setOpen(false))}
        </div>
      )}
    </div>
  )
}

export function MenuItem({
  onClick,
  children,
  danger = false,
}: {
  onClick: () => void
  children: ReactNode
  danger?: boolean
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-3 px-4 py-3 text-left text-[15px] font-bold',
        'hover:bg-surface-hover',
        danger ? 'text-danger' : 'text-ink',
      )}
    >
      {children}
    </button>
  )
}
